import { types, Instance } from 'mobx-state-tree';
import { SegmentTypes } from '@/stores/workoutPlayer/types';
import { STRETCH_SESSION_TEMPLATE_TYPES } from '@/constants/workoutTemplateTypes';
import { nowMs } from '@/services/datetime';

// ─── Segment type keys for MST enumeration ─────────────────────
const segmentTypeKeys = Object.keys(SegmentTypes) as SegmentTypes[];

// ─── SegmentInfoModel ───────────────────────────────────────────
export const SegmentInfoModel = types
  .model('SegmentInfoModel', {
    unique_name: types.string,
    name: types.string,
    segment_type: types.enumeration('SegmentType', segmentTypeKeys),
    videoUrls: types.array(types.string),
    videoHashes: types.array(types.string),
    videoSizes: types.array(types.number),
    preview_time_ms: types.number,
    completion_time_ms: types.number,
    equipment: types.optional(types.array(types.string), []),
  })
  .views((self) => ({
    get isRest(): boolean {
      return self.segment_type === SegmentTypes.REST;
    },
    get isExercise(): boolean {
      return self.segment_type === SegmentTypes.EXERCISE;
    },
    get isWarmUp(): boolean {
      return self.segment_type === SegmentTypes.WARM_UP;
    },
    get isCoolDown(): boolean {
      return self.segment_type === SegmentTypes.COOL_DOWN;
    },
    get isRecoverStretch(): boolean {
      return self.segment_type === SegmentTypes.RECOVER_STRETCH;
    },
    get isTimedSegment(): boolean {
      return (
        self.segment_type === SegmentTypes.REST ||
        self.segment_type === SegmentTypes.WARM_UP ||
        self.segment_type === SegmentTypes.COOL_DOWN ||
        self.segment_type === SegmentTypes.RECOVER_STRETCH
      );
    },
  }));

export type ISegmentInfoModel = Instance<typeof SegmentInfoModel>;

// ─── WorkoutSegmentModel ────────────────────────────────────────
export const WorkoutSegmentModel = types
  .model('WorkoutSegmentModel', {
    id: types.number,
    repetitions: types.maybeNull(types.number),
    group: types.string,
    segment_info: SegmentInfoModel,
    started_at: types.maybeNull(types.number),
    completed_at: types.maybeNull(types.number),
    resumed_at: types.maybeNull(types.number),
    has_skipped: false,
  })
  .views((self) => ({
    get isRest(): boolean {
      return self.segment_info.isRest;
    },
    get isExercise(): boolean {
      return self.segment_info.isExercise;
    },
    get isTimedSegment(): boolean {
      return self.segment_info.isTimedSegment;
    },
  }))
  .actions((self) => ({
    markStarted() {
      if (!self.started_at) {
        self.started_at = nowMs();
      }
    },
    markCompleted(hasSkipped: boolean) {
      self.completed_at = nowMs();
      self.has_skipped = hasSkipped;
    },
  }));

export type IWorkoutSegmentModel = Instance<typeof WorkoutSegmentModel>;

// ─── WorkoutModel ───────────────────────────────────────────────
const WorkoutModel = types
  .model('WorkoutModel', {
    id: types.number,
    workout_template_type: types.string,
    segments: types.array(WorkoutSegmentModel),
    started_at: types.maybeNull(types.number),
    completed_at: types.maybeNull(types.number),
  })
  .views((self) => ({
    get isStretchSession(): boolean {
      return STRETCH_SESSION_TEMPLATE_TYPES.includes(self.workout_template_type);
    },
    get segmentCount(): number {
      return self.segments.length;
    },
    get totalExercises(): number {
      return self.segments.filter((s) =>
        this.isStretchSession ? !s.segment_info.isRest : s.segment_info.isExercise,
      ).length;
    },
    segmentAtIndex(index: number): IWorkoutSegmentModel | null {
      if (index < 0 || index >= self.segments.length) return null;
      return self.segments[index];
    },
  }));

export type IWorkoutModel = Instance<typeof WorkoutModel>;

export default WorkoutModel;
